import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import Breadcrumbs from '../Components/Breadcrumbs';
import Button from '../Components/Buttons';
import PageLoader from '../Components/PageLoader';
import Table from '../Components/Tables';
import VoiceProfileTestChat from '../Components/VoiceProfileTestChat';
import type { VoiceProfileChatMessage } from '../Components/VoiceProfileTestChat';

import { useAlert } from '../Components/AlertContext';
import { AgentApiProvider, useAgentApi } from '../Services/AgentApiProvider';
import { VoiceProfileApiProvider, useVoiceProfileApi } from '../Services/VoiceProfileApiProvider';
import {
  VoiceProfileUpdateApi,
  RealtimeSessionShortApi,
} from '../Services/VoiceProfileApiProviderTypes';
import VoiceProfileForm from './VoiceProfileForm';
import {
  VoiceProfileForm as FormState,
  INITIAL_FORM_STATE,
  getChangedFields,
  sessionColumns,
  validate,
} from './VoiceProfileHelpers';
import { getTablePaginationFromStorage } from '../utils/tablePaginationStorage';
import { formatTableTitle } from '../utils/formatTitle';

const formatTime = (timestamp?: string | null) =>
  new Date(timestamp || Date.now()).toLocaleTimeString().slice(0, 8);

function VoiceProfileDetailsContent() {
  const navigate = useNavigate();

  const { organizationId, voiceProfileId } = useParams();
  const { showAlert } = useAlert();

  const { getVoiceProfileById, updateVoiceProfile, getSessions, getSessionMessages } =
    useVoiceProfileApi();
  const { getAgents, getAssistantOptions } = useAgentApi();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [agents, setAgents] = useState<{ key: string; name: string }[]>([]);
  const [voices, setVoices] = useState<{ value: string; label: string }[]>([]);

  const [form, setForm] = useState<FormState>(INITIAL_FORM_STATE);
  const [initialForm, setInitialForm] = useState<FormState | null>(null);

  // Sessions
  const [sessions, setSessions] = useState<RealtimeSessionShortApi[]>([]);
  const [totalSessions, setTotalSessions] = useState(0);

  // Active chat session state.
  // - chatOpen: whether the chat panel is shown
  // - activeSessionKey: existing session key, or undefined for a brand new session
  // - initialMessages: messages preloaded when opening an existing session
  const [chatOpen, setChatOpen] = useState(false);
  const [activeSessionKey, setActiveSessionKey] = useState<string | undefined>(undefined);
  const [initialMessages, setInitialMessages] = useState<VoiceProfileChatMessage[]>([]);
  const [loadingSession, setLoadingSession] = useState(false);

  const sessionsTitle = 'Sessions';
  const { persistedPageSize, persistedSkip } = getTablePaginationFromStorage(
    formatTableTitle(sessionsTitle),
  );

  const fetchSessions = async (skip: number, limit: number) => {
    if (!voiceProfileId) return;
    const data = await getSessions(voiceProfileId, skip, limit);
    setSessions(data.items);
    setTotalSessions(data.totalCount);
  };

  useEffect(() => {
    const load = async () => {
      if (!voiceProfileId) return;

      try {
        setLoading(true);

        const [voiceProfile, agentsData, options] = await Promise.all([
          getVoiceProfileById(voiceProfileId),
          getAgents(0, 1000),
          getAssistantOptions(),
          fetchSessions(persistedSkip, persistedPageSize),
        ]);

        const formData: FormState = {
          name: voiceProfile.name ?? '',
          assistantKey: voiceProfile.assistant_key ?? '',
          voice: voiceProfile.voice ?? '',
          voiceSpeed: voiceProfile.voice_speed ?? INITIAL_FORM_STATE.voiceSpeed,
          vadThreshold: voiceProfile.vad_threshold ?? INITIAL_FORM_STATE.vadThreshold,
          vadSilence: voiceProfile.vad_silence ?? INITIAL_FORM_STATE.vadSilence,
          isActive: voiceProfile.is_active ?? INITIAL_FORM_STATE.isActive,
        };

        setForm(formData);
        setInitialForm(formData);
        setAgents(agentsData.items.map((agent) => ({ key: agent.key, name: agent.name })));
        setVoices(options.voices);
      } catch (error) {
        showAlert('fail', 'Failed to fetch voice profile');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [voiceProfileId]);

  const onSave = async () => {
    if (!voiceProfileId || !initialForm) return;
    if (!validate(form, showAlert)) return;

    const changes: VoiceProfileUpdateApi = getChangedFields(initialForm, form);

    if (!Object.keys(changes).length) {
      showAlert('fail', 'No changes to save');
      return;
    }

    try {
      setSaving(true);
      await updateVoiceProfile(voiceProfileId, changes);
      setInitialForm(form);
      showAlert('success', 'Voice profile was successfully updated!');
    } catch (error) {
      showAlert('fail', 'Failed to update voice profile');
    } finally {
      setSaving(false);
    }
  };

  const onCancel = () => {
    navigate(`/organizations/${organizationId}/voice-profiles`, { state: { organizationId } });
  };

  // Start a brand new session — open an empty chat, no session key yet
  const onNewSession = () => {
    setActiveSessionKey(undefined);
    setInitialMessages([]);
    setChatOpen(true);
  };

  // Open an existing session — preload the latest messages and pass the session key
  const onOpenSession = async (row: RealtimeSessionShortApi) => {
    if (!voiceProfileId) return;

    try {
      setLoadingSession(true);

      const data = await getSessionMessages(voiceProfileId, row.key, 0, 50);

      const loaded: VoiceProfileChatMessage[] = [...data.items]
        .sort((a, b) => a.order_index - b.order_index)
        .map((m) => ({
          from: m.role === 'user' ? 'user' : 'assistant',
          time: formatTime(m.created_at),
          rawText: m.text ?? '',
        }));

      setInitialMessages(loaded);
      setActiveSessionKey(row.key);
      setChatOpen(true);
    } catch (error) {
      showAlert('fail', 'Failed to load session messages');
    } finally {
      setLoadingSession(false);
    }
  };

  if (loading) {
    return <PageLoader />;
  }

  return (
    <div className="container">
      <Breadcrumbs />
      <div className="row d-flex gap">
        <div className="col-6 radius-yes p-2 bg-yes">
          <h2 className="h2-title">{form.name}</h2>
          <div className="gap d-flex flex-row-to-col flex-column">
            <div className="int-col-3 p-2 border-yes radius-yes full-width-on-sml-scr">
              <h4 className="h4-title">Main Settings</h4>
              <VoiceProfileForm form={form} agents={agents} voices={voices} onChange={setForm} />

              <div className="input-block d-flex flex-column justify-between">
                <Table
                  title={sessionsTitle}
                  columns={sessionColumns}
                  data={sessions}
                  selectableRowsHighlight
                  onRowClicked={onOpenSession}
                  paginationPerPage={persistedPageSize}
                  paginationTotalRows={totalSessions}
                  onChangePage={fetchSessions}
                />
              </div>
            </div>
            <div className="buttons-container justify-end gap-12">
              <Button background="color2" size="medium" disabled={saving} onClick={onSave}>
                {saving ? 'Saving...' : 'Save'}
              </Button>
              <Button variant="danger" size="medium" onClick={onCancel}>
                Cancel
              </Button>
            </div>
          </div>
        </div>

        {voiceProfileId && form.assistantKey && (
          <div className="radius-yes p-2 bg-yes col-6">
            <div className="d-flex justify-between align-center">
              <h2 className="h2-title m-0">Test Voice Profile</h2>
              <Button background="color2" size="medium" onClick={onNewSession}>
                New Session
              </Button>
            </div>

            {loadingSession ? (
              <PageLoader />
            ) : chatOpen ? (
              <div className="gap d-flex border-yes radius-yes flex-row-to-col flex-column">
                <VoiceProfileTestChat
                  key={activeSessionKey ?? 'new-session'}
                  voiceProfileId={voiceProfileId}
                  assistantKey={form.assistantKey}
                  sessionKey={activeSessionKey}
                  initialMessages={initialMessages}
                />
              </div>
            ) : (
              <div className="small-font p-2">
                Select a session from the list or start a New Session.
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function VoiceProfileDetails() {
  return (
    <AgentApiProvider>
      <VoiceProfileApiProvider>
        <VoiceProfileDetailsContent />
      </VoiceProfileApiProvider>
    </AgentApiProvider>
  );
}

export default VoiceProfileDetails;
